import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { OrganizationUnitApiService, OrganizationUnitProfileDto } from '@core/api/services/organization-unit-api.service';
import { OrganizationUnitTreeNodeDto, OrganizationUnitByLevelDto, ChangeParentRequest } from '@core/api/services/organization-unit-api.service';
import { UpdateUnitRequest, CreateScheduleRequest } from '@core/api/generated/api-client';
import { CreateUnitRequest } from '@core/api/services/organization-unit-api.service';
import { DivineServiceApiService } from '@core/api/services/divine-service-api.service';
import { SpiritualAssistanceVisit, SpiritualAssistanceVisitApiService } from '@core/api/services/spiritual-assistance-visit-api.service';
import { AppTelemetryService } from '@core/observability/app-telemetry.service';
import { mapOrganizationUnit } from '@core/api/mappers/organization-unit.mapper';
import { mapPersonToMember } from '@core/api/mappers/person.mapper';
import { mapMinistryGroup, mapPersonMinistryToMinistries } from '@core/api/mappers/ministry.mapper';
import { OrganizationUnit, MinistryGroup, MinistryPersonGroupDto, Member, MemberPreview, MemberRow, MemberLastAttendance, AttendanceKPI, DivineServiceSummary, UnitSchedule, UnitEvent, ParentChangeHistory, MinistryTransferRequest, TransferRequestStatus } from '../models/unit.model';
import { OrganizationUnitDto } from '@shared/services/organization-unit.service';

function parseDateLocal(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function calculateAge(birthDate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return Math.max(0, age);
}

@Injectable({
  providedIn: 'root'
})
export class UnitService {
  /** @deprecated Only used for /kpis until backend exposes it in OpenAPI */
  private readonly legacyApiUrl = `${environment.apiUrl}/organizationunit`;

  constructor(
    private readonly api: OrganizationUnitApiService,
    private readonly divineServiceApi: DivineServiceApiService,
    private readonly spiritualAssistanceVisitApi: SpiritualAssistanceVisitApiService,
    private readonly http: HttpClient,
    private readonly telemetry: AppTelemetryService,
  ) {}

  getUnit(unitId: string): Observable<OrganizationUnit> {
    return this.http.get<OrganizationUnitProfileDto>(`${this.legacyApiUrl}/${unitId}`).pipe(
      map(dto => mapOrganizationUnit(dto))
    );
  }

  getChildUnitsNested(unitId: string): Observable<OrganizationUnit[]> {
    return this.api.getDescendantTree(unitId).pipe(
      map(tree => (tree as any).children ?? [] as OrganizationUnitTreeNodeDto[]),
      map(children => (children as OrganizationUnitTreeNodeDto[]).map(c => mapOrganizationUnit(c)))
    );
  }

  getMinistries(unitId: string): Observable<MinistryPersonGroupDto[]> {
    return this.api.getMinistriesByUnit(unitId).pipe(
      map(groups => groups.map(g => mapMinistryGroup(g)))
    );
  }

  getMinistriesGroupedByMinistry(unitId: string): Observable<MinistryGroup[]> {
    return this.getMinistries(unitId).pipe(
      map(groups => groups.map(group => ({
        ministry: group.ministryName as MinistryGroup['ministry'],
        ministries: group.people.map(person => mapPersonMinistryToMinistries(person))
      })))
    );
  }

  getMinistryTransferRequests(unitId: string, status: TransferRequestStatus = 'Pending'): Observable<MinistryTransferRequest[]> {
    return this.http.get<MinistryTransferRequest[]>(
      `${environment.apiUrl}/organizationunit/${unitId}/ministry-transfer-requests`,
      { params: { status } }
    );
  }

  getAllMinistryTransferRequests(status?: TransferRequestStatus): Observable<MinistryTransferRequest[]> {
    return this.http.get<MinistryTransferRequest[]>(
      `${environment.apiUrl}/ministry-transfer-requests`,
      status ? { params: { status } } : undefined
    );
  }

  approveMinistryTransferRequest(requestId: string, resolutionNotes?: string | null): Observable<void> {
    return this.telemetry.trackOperation(
      this.http.post<void>(
        `${environment.apiUrl}/ministry-transfer-requests/${requestId}/approve`,
        { resolutionNotes: resolutionNotes ?? null }
      ),
      {
        successEventName: 'ministry_transfer.approved',
        attributes: { requestId },
      },
    );
  }

  rejectMinistryTransferRequest(requestId: string, resolutionNotes?: string | null): Observable<void> {
    return this.telemetry.trackOperation(
      this.http.post<void>(
        `${environment.apiUrl}/ministry-transfer-requests/${requestId}/reject`,
        { resolutionNotes: resolutionNotes ?? null }
      ),
      {
        successEventName: 'ministry_transfer.rejected',
        attributes: { requestId },
      },
    );
  }

  cancelMinistryTransferRequest(requestId: string, resolutionNotes?: string | null): Observable<void> {
    return this.telemetry.trackOperation(
      this.http.post<void>(
        `${environment.apiUrl}/ministry-transfer-requests/${requestId}/cancel`,
        { resolutionNotes: resolutionNotes ?? null }
      ),
      {
        successEventName: 'ministry_transfer.cancelled',
        attributes: { requestId },
      },
    );
  }

  getMembers(unitId: string): Observable<Member[]> {
    return this.api.getMembersByUnit(unitId).pipe(
      map(people => people.map(p => mapPersonToMember(p)))
    );
  }

  getMemberPreview(unitId: string, page = 1, pageSize = 5): Observable<MemberPreview> {
    return this.http
      .get<{
        items?: Array<{ id?: string; firstName?: string | null; lastName?: string | null }>;
        totalCount?: number;
      }>(`${environment.apiUrl}/organizationunit/${unitId}/members/preview`, { params: { page, pageSize } })
      .pipe(
        map(response => ({
          items: (response.items ?? []).map(item => ({
            id: item.id ?? '',
            firstName: item.firstName ?? '',
            lastName: item.lastName ?? '',
          })),
          totalCount: response.totalCount ?? 0,
        }))
      );
  }

  getMemberLastAttendance(unitId: string, pageSize = 10): Observable<MemberLastAttendance[]> {
    return this.http
      .get<Array<{
        personId: string;
        firstName: string;
        lastName: string;
        communityName?: string | null;
        lastAttendanceAt?: string | null;
        divineServiceId?: string | null;
        divineServiceTypeName?: string | null;
        hostUnitName?: string | null;
      }>>(`${environment.apiUrl}/organizationunit/${unitId}/members/last-attendance`, { params: { pageSize } })
      .pipe(
        map(items => items.map(item => ({
          personId: item.personId,
          firstName: item.firstName,
          lastName: item.lastName,
          communityName: item.communityName ?? undefined,
          lastAttendanceAt: item.lastAttendanceAt ? new Date(item.lastAttendanceAt) : undefined,
          divineServiceId: item.divineServiceId ?? undefined,
          divineServiceTypeName: item.divineServiceTypeName ?? undefined,
          hostUnitName: item.hostUnitName ?? undefined,
        })))
      );
  }

  getLatestSpiritualAssistanceVisits(unitId: string, limit = 5): Observable<SpiritualAssistanceVisit[]> {
    return this.spiritualAssistanceVisitApi.list(unitId).pipe(
      map(visits => [...visits]
        .sort((left, right) => right.visitDate.localeCompare(left.visitDate))
        .slice(0, limit))
    );
  }

  getMembersFull(unitId: string, includeDeparted = false): Observable<MemberRow[]> {
    return this.http.get<any[]>(
      `${environment.apiUrl}/organizationunit/${unitId}/members`,
      { params: { includeDeparted } },
    ).pipe(
      map(people => people.map(dto => {
        const birthDate = dto.birthDate ? parseDateLocal(dto.birthDate) : undefined;
        return {
          id: dto.id ?? '',
          photo: dto.photoUrl ?? undefined,
          firstName: dto.firstName ?? '',
          lastName: dto.lastName ?? '',
          status: dto.status ?? undefined,
          isHomeAttended: dto.isHomeAttended === true,
          phone: dto.phone ?? undefined,
          birthDate,
          age: birthDate ? calculateAge(birthDate) : undefined,
          ageStatus: dto.ageStatus ?? undefined,
          ministryName: dto.ministry?.ministryName ?? undefined,
          ministrySortOrder: dto.ministry?.sortOrder ?? undefined,
          ministryStatus: dto.ministry?.status ?? undefined,
          activeMinistryUnitIds: (dto.ministry?.activities ?? [])
            .map((activity: { unitId?: string | null }) => activity.unitId)
            .filter((unitId: string | null | undefined): unitId is string => !!unitId),
          communityName: dto.membershipUnitName ?? undefined,
          communityUnitId: dto.membershipUnitId ?? undefined,
          responsiblePriestId: (dto as any).responsiblePriestId ?? undefined,
          responsiblePriestName: (dto as any).responsiblePriestName ?? undefined,
          activeAssignments: (dto.assignments ?? []).map((assignment: { assignmentName?: string; unitId?: string; unitName?: string }) => ({
            assignmentName: assignment.assignmentName ?? '',
            unitId: assignment.unitId ?? '',
            unitName: assignment.unitName ?? '',
          })),
          hasLinkedUser: dto.hasLinkedUser === true,
        } as MemberRow;
      }))
    );
  }

  /**
   * Obtiene los últimos SD de la unidad, ordenados por fecha descendente,
   * y los mapea a un resumen de concurrencia.
   */
  getDivineServices(unitId: string, pageSize = 10, endDate?: string): Observable<DivineServiceSummary[]> {
    return this.divineServiceApi.getDivineServices(unitId, null, endDate ?? null, 'date', 'desc', 1, pageSize).pipe(
      map(response => (response.items ?? []).map(dto => ({
        id: dto.id ?? '',
        date: new Date(dto.startAt!),
        unitName: dto.organizationUnitName ?? undefined,
        adults: dto.totalAdults ?? 0,
        minors: dto.totalMinors ?? 0,
        total: (dto.totalAdults ?? 0) + (dto.totalMinors ?? 0),
      })))
    );
  }

  getLatestDivineServices(unitId: string, pageSize = 2, today = new Date()): Observable<DivineServiceSummary[]> {
    const endDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return this.getDivineServices(unitId, pageSize, endDate);
  }

  /** @deprecated Usar getDivineServices() */
  getAttendanceKPIs(unitId: string): Observable<AttendanceKPI[]> {
    return this.http.get<AttendanceKPI[]>(`${this.legacyApiUrl}/${unitId}/kpis`);
  }

  updateUnit(unitId: string, data: UpdateUnitRequest): Observable<OrganizationUnit> {
    return this.api.update(unitId, data).pipe(
      map(dto => mapOrganizationUnit(dto))
    );
  }

  createUnit(data: CreateUnitRequest): Observable<OrganizationUnit> {
    return this.api.create(data).pipe(
      map(dto => mapOrganizationUnit(dto as any))
    );
  }

  searchUnits(searchTerm: string, maxLevelId?: number): Observable<{ items: OrganizationUnitDto[]; totalCount: number }> {
    return this.api.search(searchTerm, 1, 20, null, maxLevelId ?? null) as unknown as Observable<{ items: OrganizationUnitDto[]; totalCount: number }>;
  }

  getHigherLevelUnits(minExcludedLevelId?: number): Observable<OrganizationUnitByLevelDto[]> {
    return this.api.getHigherLevelUnits(minExcludedLevelId) as unknown as Observable<OrganizationUnitByLevelDto[]>;
  }

  getCommunities(
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    sortBy?: string,
    sortDirection?: 'asc' | 'desc',
    areaId?: string,
    districtId?: string,
    groupId?: string
  ): Observable<{ items: OrganizationUnitDto[]; totalCount: number; totalPages: number }> {
    return this.api.getCommunities(
      searchTerm ?? null,
      sortBy ?? null,
      sortDirection ?? null,
      page,
      pageSize,
      areaId ?? null,
      districtId ?? null,
      groupId ?? null,
    ) as unknown as Observable<{ items: OrganizationUnitDto[]; totalCount: number; totalPages: number }>;
  }

  deleteUnit(unitId: string, reason?: string): Observable<void> {
    return this.api.deleteUnit(unitId, reason);
  }

  getSchedules(unitId: string): Observable<UnitSchedule[]> {
    return this.api.getSchedules(unitId).pipe(
      map(dtos => dtos.map(d => ({
        id: d.id!,
        dayOfWeek: d.dayOfWeek!,
        startTime: d.startTime!,
        notes: d.notes ?? undefined,
      })))
    );
  }

  createSchedule(unitId: string, data: { dayOfWeek: number; startTime: string; notes?: string }): Observable<UnitSchedule> {
    const request = new CreateScheduleRequest();
    request.dayOfWeek = data.dayOfWeek;
    request.startTime = data.startTime.length === 5 ? `${data.startTime}:00` : data.startTime;
    request.notes = data.notes;
    return this.api.createSchedule(unitId, request).pipe(
      map(d => ({
        id: d.id!,
        dayOfWeek: d.dayOfWeek!,
        startTime: d.startTime!,
        notes: d.notes ?? undefined,
      }))
    );
  }

  deleteSchedule(unitId: string, scheduleId: number): Observable<void> {
    return this.api.deleteSchedule(unitId, scheduleId);
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  getEvents(unitId: string): Observable<UnitEvent[]> {
    return this.http.get<UnitEvent[]>(`${environment.apiUrl}/OrganizationUnit/${unitId}/events`);
  }

  createEvent(unitId: string, data: { eventTypeId: number; eventDate: string; notes?: string }): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/OrganizationUnit/${unitId}/events`, data);
  }

  updateEvent(unitId: string, eventId: number, data: { eventTypeId: number; eventDate: string; notes?: string }): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}/OrganizationUnit/${unitId}/events/${eventId}`, data);
  }

  deleteEvent(unitId: string, eventId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/OrganizationUnit/${unitId}/events/${eventId}`);
  }

  changeParent(unitId: string, request: ChangeParentRequest): Observable<OrganizationUnit> {
    return this.api.changeParent(unitId, request).pipe(
      map(dto => mapOrganizationUnit(dto as any))
    );
  }

  getParentChangeHistory(unitId: string): Observable<ParentChangeHistory[]> {
    return this.http.get<ParentChangeHistory[]>(`${environment.apiUrl}/OrganizationUnit/${unitId}/parent-history`);
  }
}
